import { useEffect, useRef, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { IconButton } from '@mui/material';

import type { ImageCarouselProps } from './imageCarouselTypes';

export const ImageCarousel = ({
  images,
  onRemoveImage,
}: ImageCarouselProps) => {
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    checkScroll();
    scrollContainer.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [images]);

  if (images.length === 0) {
    return (
      <div className="vai-relative vai-h-[100px] vai-w-full vai-bg-black vai-rounded-md vai-flex vai-items-center vai-justify-center">
        <p className="vai-text-white vai-text-sm">
          <Trans>No images selected</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="vai-relative vai-w-full">
      <div
        ref={scrollContainerRef}
        className="vai-flex vai-gap-2 vai-overflow-x-auto vai-px-2 vai-py-3 vai-bg-gray-50 vai-rounded-md vai-scrollbar-thin vai-scrollbar-thumb-gray-400 vai-scrollbar-track-transparent vai-snap-x vai-snap-mandatory vai-snap-center"
      >
        {images.map((image, index) => (
          <div key={index} className="vai-relative vai-flex-shrink-0">
            <img
              src={image.path}
              alt={`${index + 1}`}
              className="vai-h-[100px] vai-w-[100px] vai-object-cover vai-rounded-md"
            />
            <IconButton
              size="small"
              onClick={(e) => onRemoveImage(image.originalIndex, e)}
              className="vai-absolute vai-top-[-8px] vai-right-[-8px] vai-bg-black/50 hover:vai-bg-black/70 vai-rounded-full vai-w-4 vai-h-4 vai-min-w-0 vai-text-sm"
              sx={{ color: 'white' }}
              data-testid="icon-button"
            >
              ×
            </IconButton>
          </div>
        ))}
      </div>
      {showLeftFade && (
        <div className="vai-pointer-events-none vai-absolute vai-inset-y-0 vai-left-0 vai-w-8 vai-bg-gradient-to-r vai-from-gray-50 vai-to-transparent" />
      )}
      {showRightFade && (
        <div className="vai-pointer-events-none vai-absolute vai-inset-y-0 vai-right-0 vai-w-8 vai-bg-gradient-to-l vai-from-gray-50 vai-to-transparent" />
      )}
    </div>
  );
};

export const ImageViewer = ({ images, onRemoveImage }: ImageCarouselProps) => {
  if (images.length === 0) return null;

  return <ImageCarousel images={images} onRemoveImage={onRemoveImage} />;
};
